import React, { useEffect, useState } from "react";
import { Fragment } from "react";
import { Translate, getTranslation } from "orion-components/i18n";
import { IconButton, Switch, Typography, Checkbox, FormControlLabel, SvgIcon, Button, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { mdiCameraControl, mdiClose } from "@mdi/js";
import PTZController from "../PTZ/PTZController";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import PropTypes from "prop-types";
import { useMemo } from "react";
import { cameraService } from "client-app-core";
import { getEntityName } from "../../../../GlobalData/Selectors";
import { startPtzPrivilegeStream, stopPtzPrivilegeStream } from "orion-components/GlobalData/Actions";
import { getCanControlPTZ, getPtzLockType, getPtzLockedOwner } from "../../../../GlobalData/PTZPrivilege/selectors";
import { updateComponentInstance } from "../../../../SharedComponents/ComponentState/redux/actions";

const propTypes = {
	collapseBottomBar: PropTypes.func.isRequired,
	camera: PropTypes.object.isRequired
};

const formControlLabelStyle = {
	"& .MuiFormControlLabel-label": {
		fontSize: "1em",
		color: "#FFFFFF"
	}
};

const useStyles = makeStyles({
	thumbOff: {
		backgroundColor: "#ffffff"
	},
	trackOff: {
		backgroundColor: "#828283",
		opacity: 1
	},
	thumbSwitched: {
		backgroundColor: "#29B6F6"
	},
	trackSwitched: {
		backgroundColor: "#bee1f1!important",
		opacity: "1!important"
	}
});

const COMPONENT_KEY = "cbComponents.newVideoPlayer.videoPlayerContainer";

const LiveBottombar = ({ collapseBottomBar, camera, instanceId, inDock }) => {
	const classes = useStyles();
	const contextPanel = useSelector(state => state.appState?.contextPanel);
	const contextualData = useSelector(state => (!inDock ? state.contextualData : {}));
	const currentContextPanelId = !inDock ? contextPanel?.contextPanelData?.selectedContext?.primary : "";
	const [features, setFeatures] = useState([]);
	const [targetEntityName, setTargetEntityName] = useState("");
	const [isSlewLocked, setIsSlewLocked] = useState(false);
	const [showPTZControls, setShowPTZControls] = useState(true); // PTZ controls displayed by default

	const contextEntity = contextualData[currentContextPanelId]?.entity;
	const dispatch = useDispatch();

	const slewLockEntityName = useSelector(state =>
		camera?.slewLock ? getEntityName(state, camera.slewLock.id, camera.slewLock.type) : ""
	);

	const canControlPTZ = useSelector(state => getCanControlPTZ(state, { cameraId: camera.id }));
	const lockType = useSelector(state => getPtzLockType(state, { cameraId: camera.id }));
	const ptzLockedOwnerName = useSelector(state => getPtzLockedOwner(state, { cameraId: camera.id }));

	// The slew lock option appears only on the track profiles.
	const enableSlewLock = !inDock && contextEntity?.entityType === "track";

	//The "Slew" option will appear on profiles other than the camera profile, which displays its own camera stream on the camera widget.
	const disableSlew = !inDock
		? contextPanel?.contextPanelData?.primaryOpen && currentContextPanelId === camera.id
		: true;

	const dir = useSelector(state => getDir(state));
	const styles = useMemo(() => {
		return {
			bottomBarSecondaryWrapper: {
				flexBasis: "90%",
				...(dir === "ltr" && { marginLeft: 6 }),
				...(dir === "rtl" && { marginRight: 6 }),
				height: "100%"
			},
			alignFlexCenter: {
				display: "flex",
				alignItems: "center"
			},
			slewIcon: {
				color: "#fff",
				padding: 0,
				boxShadow: "0px 2px 3px #00000029",
				width: 59,
				height: 22,
				borderRadius: 5,
				opacity: 1,
				background: "#4DB5F4 0% 0% no-repeat padding-box",
				...(dir === "ltr" && { marginLeft: 30 }),
				...(dir === "rtl" && { marginRight: 30 }),
				fontSize: "1em"
			},
			slewLockWrapper: {
				...(dir === "ltr" && { marginLeft: 20 }),
				...(dir === "rtl" && { marginRight: 20 }),
				display: "flex",
				alignItems: "center"
			},
			closeIconWrapper: {
				flexBasis: "10%",
				textAlign: "end",
				display: "flex",
				alignItems: "center"
			},
			closeIcon: {
				...(dir === "ltr" && { marginRight: 10, marginLeft: "auto" }),
				...(dir === "rtl" && { marginLeft: 10, marginRight: "auto" }),
				color: "black",
				padding: 0,
				boxShadow: "0px 0px 3px #00000026",
				width: 14,
				height: 14,
				borderRadius: "50%",
				opacity: 1,
				transform: "matrix(0, 1, -1, 0, 0, 0)",
				backgroundColor: "#FFFFFF"
			}
		};
	}, [dir]);

	const togglePTZControls = () => setShowPTZControls(prev => !prev);

	const toggleSlew = e => {
		const slewLock = !isSlewLocked;
		setIsSlewLocked(slewLock);
		handleSlewLock(e, slewLock ? "lock" : "release");
	};

	useEffect(() => {
		if (camera?.entityData?.properties) {
			setFeatures(camera.entityData.properties.features ?? []);
		}
	}, [camera]);

	// Keep slew lock + target name in sync with props / store
	useEffect(() => {
		if (camera?.slewLock) {
			// If current context entity is the lock target, trust its name
			if (contextEntity && camera.slewLock.id === contextEntity.id) {
				setTargetEntityName(contextEntity?.entityData?.properties?.name || "");
			} else {
				// Otherwise use global selector result
				setTargetEntityName(slewLockEntityName || "");
			}
			setIsSlewLocked(true);
		} else {
			setIsSlewLocked(false);
			setTargetEntityName(contextEntity?.entityData?.properties?.name || "");
		}
	}, [camera?.slewLock, contextEntity, slewLockEntityName]);

	const handleSlew = e => {
		e.stopPropagation();
		const { coordinates } = contextEntity.entityData.geometry;
		// Params: cameraId, latitude, longitude, altitude, speed (0 to 1), callback
		cameraService.moveGeo(camera.id, coordinates[1], coordinates[0], 0, 1);
	};

	const handleSlewLock = (e, status) => {
		const { id, entityType, entityData } = contextEntity;
		e.stopPropagation();
		if (status === "release") {
			cameraService.releaseSlewLock(camera.id, (err, response) => {
				if (err) {
					console.log(err, response);
				}
			});
		}
		if (status === "lock") {
			cameraService.setSlewLock(camera.id, id, entityType, entityData.geometry, (err, response) => {
				if (err) {
					console.log(err, response);
				}
			});
			setTargetEntityName(entityData.properties.name);
		}
	};

	useEffect(() => {
		dispatch(startPtzPrivilegeStream(camera.id, instanceId));

		return () => {
			dispatch(stopPtzPrivilegeStream(camera.id, instanceId));
		};
	}, [camera.id, instanceId, dispatch]);

	return (
		<Fragment>
			<div className="bccVideoBottomBarWrapper" style={{ display: "flex !important", zIndex: 1901 }}>
				<div style={styles.bottomBarSecondaryWrapper}>
					<div style={{ display: "flex", height: "100%" }}>
						{canControlPTZ ? (
							<div style={{ ...styles.alignFlexCenter, fontSize: 12 }}>
								<Tooltip
									title={getTranslation(
										`global.CBComponents.newVideoPlayer.components.videoBottomBar.${
											showPTZControls ? "hidePtzControls" : "showPtzControls"
										}`
									)}
								>
									<Button
										variant="outlined"
										onClick={togglePTZControls}
										startIcon={
											<SvgIcon
												style={{
													width: "20px",
													height: "20px",
													color: showPTZControls ? "#4DB5F4" : "#fff"
												}}
											>
												<path d={mdiCameraControl} />
											</SvgIcon>
										}
										style={{
											color: showPTZControls ? "#4DB5F4" : "#fff",
											borderColor: showPTZControls ? "#4DB5F4" : "#fff", // outline color
											fontSize: "1em",
											width: "60px",
											height: "25px"
										}}
									>
										<Translate value="global.CBComponents.newVideoPlayer.components.videoBottomBar.ptz" />
									</Button>
								</Tooltip>
							</div>
						) : (
							<></>
						)}
						{!canControlPTZ && (lockType === "explicit" || lockType === "temporary") ? (
							<div
								style={{
									...styles.alignFlexCenter,
									maxWidth: 140
								}}
							>
								<Tooltip
									placement="top"
									title={
										<Translate
											value={`global.CBComponents.newVideoPlayer.components.videoBottomBar.${lockType === "temporary" ? "inUseBy" : "lockedBy"}`}
											name={ptzLockedOwnerName}
										/>
									}
								>
									<span
										style={{
											fontSize: 12,
											color: "#FFF",
											display: "inline-block",
											maxWidth: "100%",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap"
										}}
									>
										<Translate
											value={`global.CBComponents.newVideoPlayer.components.videoBottomBar.${lockType === "temporary" ? "inUseBy" : "lockedBy"}`}
											name={ptzLockedOwnerName}
										/>
									</span>
								</Tooltip>
							</div>
						) : null}

						{features.includes("auto-slew") && (
							<>
								{!isSlewLocked && !disableSlew && (
									<div style={{ ...styles.alignFlexCenter, fontSize: 10 }}>
										<Tooltip
											title={getTranslation(
												"global.CBComponents.newVideoPlayer.components.videoBottomBar.slewCameraTo",
												"",
												targetEntityName
											)}
										>
											<IconButton style={styles.slewIcon} onClick={handleSlew}>
												<img
													alt="slew"
													src={require(`orion-components/img/app-icons/icon-slew.png`)}
												/>
												<Typography sx={{ fontSize: "1em" }}>
													<Translate value="global.CBComponents.newVideoPlayer.components.videoBottomBar.slew" />
												</Typography>
											</IconButton>
										</Tooltip>
									</div>
								)}
								{enableSlewLock && (
									<div style={{ ...styles.slewLockWrapper }}>
										<Tooltip
											title={getTranslation(
												`global.CBComponents.newVideoPlayer.components.videoBottomBar.${
													isSlewLocked ? "stopCameraTrackingTo" : "startCameraTrackingTo"
												}`,
												"",
												targetEntityName
											)}
										>
											<Button
												variant="text"
												{...(isSlewLocked && { disableRipple: true, disableFocusRipple: true })}
												disableElevation
												sx={{
													textTransform: "none",
													fontSize: 11,
													color: isSlewLocked ? "#fff" : "#4DB5F4",
													"&:hover": {
														backgroundColor: "transparent" // removes hover effect on the button
													},
													"& .MuiButton-endIcon": {
														marginLeft: "2px"
													}
												}}
												{...(!isSlewLocked && { onClick: toggleSlew })}
												endIcon={
													isSlewLocked && (
														<SvgIcon
															sx={{
																width: 17.5,
																height: 17.5,
																color: "#fb0707e7",
																cursor: "pointer"
															}}
															onClick={e => {
																toggleSlew(e);
															}}
														>
															<path d={mdiClose} />
														</SvgIcon>
													)
												}
											>
												{getTranslation(
													`global.CBComponents.newVideoPlayer.components.videoBottomBar.${
														isSlewLocked ? "tracking" : "trackTarget"
													}`,
													"",
													targetEntityName
												)}
											</Button>
										</Tooltip>
									</div>
								)}
							</>
						)}
					</div>
				</div>
				<div style={styles.closeIconWrapper}>
					<IconButton
						style={styles.closeIcon}
						onClick={() => {
							dispatch(
								updateComponentInstance(COMPONENT_KEY, instanceId, {
									components: { mainMenu: { showControls: false } }
								})
							);
						}}
					>
						<CloseIcon sx={{ width: 15, height: 15 }} />
					</IconButton>
				</div>
			</div>

			{showPTZControls & canControlPTZ ? (
				<PTZController cameraId={camera.id} camera={camera} instanceId={instanceId} />
			) : (
				<></>
			)}
		</Fragment>
	);
};

LiveBottombar.propTypes = propTypes;

export default LiveBottombar;
